/* eslint-disable no-custom-animation/no-custom-animation */

import type { TooltipProps, UnstyledButtonProps } from '@mantine/core';
import { Loader, UnstyledButton, createStyles } from '@mantine/core';
import { rem } from '@mantine/styles';
import { buttonHoverTransition } from '@repo/theme/primitives';
import type { ColorNames } from '@repo/theme/utils';
import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import { Icon, type IconNames } from '../Icon';
import { WithConditionalTooltip } from '../Tooltip';

export type IconButtonVariants = 'default' | 'primary' | 'tertiary';
export type IconButtonTones = 'default' | 'critical';
export type IconButtonSizes = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export interface IconButtonProps extends UnstyledButtonProps {
	/** The icon at the center of the button */
	iconName: IconNames;
	/** Controls button appearance  */
	variant?: IconButtonVariants;
	/** Controls button appearance  */
	tone?: IconButtonTones;
	/** Predefined button size */
	size?: IconButtonSizes;
	/** Callback for when it's clicked */
	onClick?: (event: React.MouseEvent<HTMLElement>) => void;
	/** Callback for when the wheel event is triggered */
	onWheel?: (event: React.WheelEvent<HTMLElement>) => void;
	/** Text to show on hover */
	tooltip?: ReactNode;
	tooltipProps?: Omit<TooltipProps, 'children' | 'label'>;
	/** Show tooltip even when button is disabled */
	showTooltipWhenDisabled?: boolean;

	/** highlight the button visually */
	highlight?: boolean;

	/** fill the icon with the same color as the stroke */
	fillIcon?: boolean;

	/** Indicate loading state */
	loading?: boolean;
	/** Disabled state */
	disabled?: boolean;
	id?: string;

	/** Gap between the icon and the text */
	includeOffset?: boolean;
}

interface IconButtonStyleProps {
	loading: boolean;
	disabled: boolean;
	size: IconButtonSizes;
	variant: IconButtonVariants;
	tone: IconButtonTones;
	fillIcon: boolean;
	showTooltipWhenDisabled: boolean;
}

const useStyles = createStyles(
	(
		theme,
		{
			size,
			loading,
			disabled,
			variant,
			tone,
			fillIcon,
			showTooltipWhenDisabled,
		}: IconButtonStyleProps
	) => {
		let [height, width, paddingY] = [
			theme.other.space[7],
			theme.other.space[7],
			theme.other.space[1.5],
		];
		let fillColor: ColorNames = 'fill/primary/default';
		let iconColor: ColorNames = 'icon/primary/default';
		let hoverFillColor: ColorNames = 'fill/transparent/hover';
		let hoverIconColor: ColorNames = 'icon/primary/hover';
		let activeFillColor: ColorNames = 'fill/primary/active';
		let activeIconColor: ColorNames = 'icon/primary/active';
		let disabledFillColor: ColorNames = 'fill/primary/disabled';
		let disabledIconColor: ColorNames = 'icon/primary/disabled';
		let boxShadow = '';
		let boxShadowOnActive = '';

		if (size === '2xl') {
			[height, width, paddingY] = [
				theme.other.space[10],
				theme.other.space[10],
				theme.other.space[2],
			];
		} else if (size === 'xl') {
			[height, width, paddingY] = [
				theme.other.space[9],
				theme.other.space[9],
				theme.other.space[2],
			];
		} else if (size === 'lg') {
			[height, width, paddingY] = [
				theme.other.space[8],
				theme.other.space[8],
				theme.other.space[1.5],
			];
		} else if (size === 'sm') {
			[height, width, paddingY] = [
				theme.other.space[6],
				theme.other.space[6],
				theme.other.space[1],
			];
		} else if (size === 'xs') {
			[height, width, paddingY] = [
				theme.other.space[5],
				theme.other.space[5],
				theme.other.space[1],
			];
		}

		if (variant === 'default') {
			fillColor = 'fill/primary/default';
			iconColor = 'icon/primary/default';
			hoverFillColor = 'fill/primary/hover';
			activeFillColor = 'fill/primary/active';
			disabledFillColor = 'fill/primary/disabled';
			disabledIconColor = 'icon/primary/disabled';
			boxShadow =
				'0px 1px 0px 0px #DDD inset, 1px 0px 0px 0px #DDD inset, -1px 0px 0px 0px #DDD inset, 0px -1px 0px 0px #CCC inset';
			boxShadowOnActive =
				'0px 2px 1px 0px rgba(0, 0, 0, 0.12) inset, 1px 1px 1px 0px rgba(0, 0, 0, 0.12) inset, -1px 0px 1px 0px rgba(0, 0, 0, 0.12) inset';
			if (tone === 'critical') {
				iconColor = 'icon/critical/default';
				hoverIconColor = 'icon/critical/default';
				activeIconColor = 'icon/critical/default';
			}
			if (loading) {
				fillColor = 'fill/primary/disabled';
			}
		} else if (variant === 'primary') {
			fillColor = 'fill/brand/default';
			hoverFillColor = 'fill/brand/hover';
			activeFillColor = 'fill/brand/active';
			iconColor = 'text/brand-on-fill/default';
			hoverIconColor = 'text/brand-on-fill/hover';
			activeIconColor = 'text/brand-on-fill/active';
			disabledFillColor = 'fill/brand/disabled';
			disabledIconColor = 'text/brand-on-fill/disabled';
			boxShadow = `0px 1px 0px 0px ${theme.other.getColor('shadow/button-shadow')} inset, 0px -1px 0px 1px ${theme.other.getColor('shadow/button-shadow')} inset, -2px 0px 0px 0px rgba(255, 255, 255, 0.20) inset, 2px 0px 0px 0px rgba(255, 255, 255, 0.20) inset, 0px 2px 0px 0px rgba(255, 255, 255, 0.20) inset`;
			boxShadowOnActive = `0px 3px 0px 0px ${theme.other.getColor('shadow/button-shadow')} inset`;
			if (tone === 'critical') {
				fillColor = 'fill/critical/default';
				hoverFillColor = 'fill/critical/hover';
				activeFillColor = 'fill/critical/active';
				iconColor = 'text/critical-on-fill/default';
				hoverIconColor = 'text/critical-on-fill/hover';
				activeIconColor = 'text/critical-on-fill/active';
				boxShadow =
					'0px 1px 0px 0px rgba(255, 255, 255, 0.48) inset, -1px 0px 0px 0px rgba(255, 255, 255, 0.20) inset, 1px 0px 0px 0px rgba(255, 255, 255, 0.20) inset, 0px -1.5px 0px 0px rgba(0, 0, 0, 0.25) inset';
				boxShadowOnActive = `0px 3px 0px 0px ${theme.other.getColor(
					'fill/critical/active'
				)} inset`;
			}
			if (loading) {
				fillColor = 'fill/brand/disabled';
			}
		} else if (variant === 'tertiary') {
			fillColor = 'fill/transparent/default';
			iconColor = 'icon/primary/default';
			hoverFillColor = 'fill/transparent/hover';
			activeFillColor = 'fill/transparent/active';
			disabledFillColor = 'fill/transparent/default';
			disabledIconColor = 'icon/primary/disabled';
		}

		return {
			container: {
				display: 'inline-block',
				height,
				width,
				padding: `${rem(paddingY)} 0`,
				position: 'relative',

				borderRadius: theme.other.space[2],

				color: theme.other.getColor(iconColor),
				fill: fillIcon ? theme.other.getColor(iconColor) : undefined,
				backgroundColor: theme.other.getColor(fillColor),

				boxShadow: loading || disabled ? 'none' : boxShadow,
				transition: 'box-shadow 0.075s ease-in-out 0s',
				...buttonHoverTransition('background-color'),

				'&:hover': {
					color: theme.other.getColor(hoverIconColor),
					backgroundColor: theme.other.getColor(hoverFillColor),
				},
				'&:active': {
					color: theme.other.getColor(activeIconColor),
					backgroundColor: theme.other.getColor(activeFillColor),
					boxShadow: boxShadowOnActive,
					paddingTop:
						variant !== 'tertiary' ? `calc(${paddingY}px + 1px)` : undefined,
					paddingBottom:
						variant !== 'tertiary' ? `calc(${paddingY}px - 1px)` : undefined,
				},
				'&[data-active]': {
					color: theme.other.getColor(activeIconColor),
					fill: fillIcon ? theme.other.getColor(activeIconColor) : undefined,
					backgroundColor: theme.other.getColor(activeFillColor),
					boxShadow: boxShadowOnActive,
					paddingTop:
						variant !== 'tertiary' ? `calc(${paddingY}px + 1px)` : undefined,
					paddingBottom:
						variant !== 'tertiary' ? `calc(${paddingY}px - 1px)` : undefined,
					'&:hover': {
						backgroundColor: theme.other.getColor(hoverFillColor),
						paddingTop: `${paddingY}px`,
						paddingBottom: `${paddingY}px`,
					},
				},
				'&:focus': {
					outline: `solid ${theme.other.getColor(
						'border/emphasis/default'
					)} 2px`,
					outlineOffset: rem(theme.other.space[0.25]),
				},
				'&:disabled': {
					borderColor: 'transparent',
					backgroundColor: theme.other.getColor(disabledFillColor),
					color: theme.other.getColor(disabledIconColor),
					cursor: 'not-allowed',
					pointerEvents: !showTooltipWhenDisabled ? 'none' : undefined,
				},
			},
			shine: {
				position: 'absolute',
				width: '100%',
				height: '100%',
				top: 0,
				left: 0,

				background:
					variant === 'primary' && theme.colorScheme !== 'dark'
						? 'linear-gradient(180deg, rgba(255, 255, 255, 0.04) 87%, rgba(255, 255, 255, 0.16) 100%)'
						: undefined,
			},
			inner: {
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				height: '100%',
				overflow: 'visible',
			},
			loader: {
				position: 'absolute',
				left: '50%',
				top: `calc(50% - ${theme.other.space[2]}px)`,
				height: '100%',
				transform: 'translateX(-50%)',
			},
			icon: {
				stroke: disabled
					? theme.other.getColor(disabledIconColor)
					: theme.other.getColor(iconColor),
				color: disabled ? theme.other.getColor(disabledIconColor) : 'inherit',
				// eslint-disable-next-line no-nested-ternary
				fill: fillIcon
					? disabled
						? theme.other.getColor(disabledIconColor)
						: 'inherit'
					: undefined,
				opacity: loading ? 0 : 1,
			},
		};
	}
);

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
	(
		{
			id,
			onClick,
			onWheel,
			variant = 'default',
			tone = 'default',
			iconName,
			size = 'md',
			loading = false,
			disabled = false,
			tooltip = undefined,
			tooltipProps = undefined,
			showTooltipWhenDisabled = false,
			highlight = false,
			fillIcon = false,
			className,
			classNames,
			styles,
			includeOffset = false,
			...other
		}: IconButtonProps,
		ref
	) => {
		const { classes, cx, theme } = useStyles({
			size,
			loading,
			disabled,
			variant,
			tone,
			fillIcon,
			showTooltipWhenDisabled,
		});

		return (
			<WithConditionalTooltip
				tooltipLabel={tooltip}
				offset={includeOffset ? 8 : undefined}
				{...(tooltipProps || {})}
			>
				<UnstyledButton
					id={id}
					ref={ref}
					aria-label={typeof tooltip === 'string' ? tooltip : undefined}
					className={cx(classes.container, className)}
					classNames={classNames}
					styles={styles}
					disabled={disabled || loading}
					data-active={highlight || undefined}
					onClick={onClick}
					onWheel={onWheel}
					{...other}
				>
					{variant === 'primary' && (
						<div className={classes.shine} role="none" />
					)}
					{loading === true && (
						<span className={classes.loader}>
							<Loader
								size="xs"
								color={theme.other.getColor(
									variant === 'primary'
										? 'text/brand-on-fill/disabled'
										: 'icon/primary/disabled'
								)}
							/>
						</span>
					)}
					<span className={classes.inner}>
						<Icon name={iconName} className={classes.icon} />
					</span>
				</UnstyledButton>
			</WithConditionalTooltip>
		);
	}
);
IconButton.displayName = 'IconButton';

export default IconButton;
